import { IAttribute, IModel } from './../base/annotation';
import { Metamodel } from '../base/model';
import { SnowflakeLinkedService } from './snowflake.linkedservice';
import { AzureKeyVault } from './key-vault';
import { ModelConstants } from '../model-constants';
import { snowflakeV2AuthTypes } from '../../interfaces/IAuthenticationType';
import { validateAKVLinkedServiceReference } from '../../shared/utils/linkedservice-validation-util';
import { shellStringResources as stringResources } from '../../Resources/ShellStringResources';
import { format, getConnectorV2Name } from '../../shared/utils/string.util';
import { isTridentInIframe } from '../../shared/utils/iframe.utils';
import { ParameterConfig } from '../pipeline/parameter-config';
import { SnowflakeAdditionalPropertiesKeyType } from '../../constants/snowflake-consts';
import { ShellLinkedServiceUtils } from './linked-service-utils';
import { Owned } from '../base/named';
import { Type } from '../../interfaces/IPipelineOutput';

/**
 * Most of the properties are the same as SnowflakeLinkedService, but the authenticationType is different.
 * So we inherit the following properties from SnowflakeLinkedService.
 * - warehouse
 * - database
 * - role
 * - user
 * - password
 * - clientid
 * - clientsecret
 * - scope
 */
@Metamodel.model({
    name: ModelConstants.snowflakeV2LinkedService,
    display: [
        format(stringResources.ModelName_LinkedServiceTemplate, getConnectorV2Name(stringResources.Snowflake_DisplayText, isTridentInIframe())),
        format(stringResources.ModelName_LinkedServicesTemplate, getConnectorV2Name(stringResources.Snowflake_DisplayText, isTridentInIframe()))
    ]
})
export class SnowflakeV2LinkedService extends SnowflakeLinkedService {

    public static readonly MODEL: IModel;
    public static readonly ACCOUNT_IDENTIFIER: IAttribute;
    public static readonly PRIVATE_KEY: IAttribute;
    public static readonly PRIVATE_KEY_PASSPHRASE: IAttribute;
    public static readonly TENANT_ID: IAttribute;
    public static readonly HOST: IAttribute;
    public static readonly TREAT_DECIMAL_AS_STRING: IAttribute;

    private _defaultAdditionalPropertyValues11: Array<Partial<ParameterConfig<SnowflakeAdditionalPropertiesKeyType>>> = [
        {
            name: 'useUtcTimestamps' as SnowflakeAdditionalPropertiesKeyType,
            value: false,
            type: Type.Boolean
        }
    ];

    @Metamodel.attribute({
        display: stringResources.ModelName_AccountName,
        association: 'ACCOUNT_IDENTIFIER',
        templatable: true
    })
    public accountIdentifier: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_AuthenticationType
    })
    public authenticationType?: string = snowflakeV2AuthTypes.Basic;

    @Metamodel.attribute({
        display: stringResources.ModelName_PrivateKey,
        association: 'PRIVATE_KEY',
        constraint: {
            validator: validateAKVLinkedServiceReference
        },
        templatable: true
    })
    public privateKey?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.ModelName_PrivateKeyPassphrase,
        association: 'PRIVATE_KEY_PASSPHRASE',
        constraint: {
            validator: validateAKVLinkedServiceReference
        },
        templatable: true
    })
    public privateKeyPassphrase?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.ModelName_Tenant,
        association: 'TENANT_ID',
        templatable: true
    })
    public tenantId?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Host,
        association: 'HOST',
        templatable: true
    })
    public host?: string;

    @Metamodel.attribute({
        display: stringResources.VersionLabel
    })
    public version?: string;

    public parameterizableAdditionalFields: Array<ParameterConfig<SnowflakeAdditionalPropertiesKeyType>> = [];

    public isLegacyConnector: boolean = false;

    constructor(name?: string, description?: string) {
        super(name, description);
        if (!isTridentInIframe()) {
            this.version = '1.1';
            this.setDefaultAdditionalProperties(this.parameterizableAdditionalFields);
        }
    }

    public setDefaultAdditionalProperties(fields: Array<ParameterConfig<SnowflakeAdditionalPropertiesKeyType>>): void {
        // by default, all newly created V2 ls should be version 1.1, thus auto populate useUtcTimestamps
        this._defaultAdditionalPropertyValues11.forEach((item) => {
            const paramConfig = Owned.create<ParameterConfig<SnowflakeAdditionalPropertiesKeyType>>(ParameterConfig.MODEL, this);
            paramConfig.name = item.name;
            paramConfig.value = item.value;
            paramConfig.type = item.type;
            fields.push(paramConfig);
        });
    }

    public convertV1toV2(context: { inlineDataset?: boolean }): SnowflakeV2LinkedService {
        this.accountIdentifier = this.accountname;
        return ShellLinkedServiceUtils.removePassword(context, this) as SnowflakeV2LinkedService;
    }
}
